import { LINE_BREAK_REGEX } from '@/constants/regex';

/**
 * Format degree and field of study for display
 * @param degree The degree (e.g., "Bachelor")
 * @param major The field of study (e.g., "Computer Science")
 * @returns Formatted string based on the provided inputs
 */
export const formatDegreeAndFieldOfStudy = (
  degree?: string,
  major?: string,
): string =>
  degree && major ? `${degree}'s in ${major}` : degree || major || '';

export const smartTrim = (text: string) =>
  /^@ ?$/.test(text) ? text : text.trim();

/**
 * Splits text into parts and marks specified parts for highlighting
 * @param text - The input text to process
 * @param highlightedParts - Array of parts to highlight (falsy values will be filtered out)
 * @returns Array of text parts with highlight status and unique keys
 */
export const normalizeTextWithHighlight = (
  text: string,
  highlightedParts: Array<string | null | undefined | false> = [],
) => {
  // Filter out falsy values from highlighted parts
  const validHighlightedParts = highlightedParts.filter(
    (part): part is string => Boolean(part),
  );

  // If no highlights, return as single part
  if (!validHighlightedParts.length) {
    return [{ text, isHighlighted: false, key: 0 }];
  }

  // Create regex pattern for all highlighted parts (escape special regex characters)
  const escapedParts = validHighlightedParts.map((part) =>
    part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  );
  const pattern = new RegExp(`(${escapedParts.join('|')})`, 'g');

  // Split text by pattern while preserving matched parts and filtering out empty strings
  const parts = text.split(pattern).filter(Boolean);

  // Convert to array with highlight information
  return parts.map((part, index) => ({
    text: part,
    isHighlighted: validHighlightedParts.includes(part),
    key: index,
  }));
};

/**
 * Normalize heading based on the length of the data
 * @param title - The title of the heading
 * @param length - The length of the data
 * @param activeCount - The number of active items (optional)
 * @returns Normalized heading
 */
export const normalizeHeading = (
  title: string,
  length: number,
  activeCount?: number,
) => {
  const shouldPluralize =
    length > 1 &&
    activeCount !== 1 &&
    title !== 'Education' &&
    title !== 'Training';
  const pluralTitle = shouldPluralize ? `${title}s` : title;
  const totalInfo = `Total: ${length}`;

  return `${pluralTitle}${activeCount !== undefined ? `: ${activeCount}` : ''} - ${totalInfo}`;
};

/**
 * Sanitizes a string to lowercase, removes spaces, and preserves special characters.
 *
 * @param {string} value - The input string to convert
 * @returns {string} A lowercase string with spaces removed and special characters preserved
 * @example
 * sanitizeString('Hello World') // returns 'helloworld'
 * sanitizeString('React-Native') // returns 'react-native'
 * sanitizeString('C# Programming') // returns 'c#programming'
 */
export const sanitizeString = (value: string): string =>
  value.toLowerCase().replace(/ /g, '');

/** Normalize string: trim and lowercase */
export const normalizeString = (s: string): string => s.trim().toLowerCase();

/** Format list with "and" before last item */
export const formatListWithAnd = (items: string[]): string => {
  const arr = items.filter(Boolean);

  return arr.length > 1
    ? `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`
    : arr.join(', ');
};

/**
 * Format list with truncation (shows up to limit, then "and X more")
 */
export const formatTruncatedList = (
  items: string[],
  limit: number,
  formatList: (items: string[]) => string = formatListWithAnd,
): string => {
  if (items.length === 0) return '';
  if (items.length <= limit) {
    return formatList(items);
  }
  return `${items.slice(0, limit).join(', ')}, and ${items.length - limit} more`;
};

/**
 * Removes trailing line breaks from a string
 * Handles all line break types: \r\n (Windows), \r (Mac), \n (Unix)
 */
export const removeTrailingLineBreaks = (text: string): string =>
  text.replace(LINE_BREAK_REGEX.TRAILING, '');
